/**
 * The shape popup — `docs/property-maps-v2.md` §5.3, board 2.
 *
 * 310px, anchored beside the selected shape: its name, what it measures, the
 * eight colours, a note, a photo, and the two buttons. Delete is in the `⋯`,
 * and confirms only for a shape carrying a note or a photo — those are the
 * ones where something somebody wrote would go with it.
 *
 * **Everything here saves as you leave the field.** There is no Save on a map
 * (§12 rule 9), so Done closes the popup and nothing else; a Save button
 * beside it would imply the colour swatch had not already taken.
 *
 * **Save to property measurements is PR 4's**, and it is drawn now only where
 * it would be drawn then — never on a marker or a label, because neither
 * measures anything, and never with the `measurements` feature off.
 */
import { useEffect, useState } from 'react'
import { LAYER_COLORS, LAYER_PALETTE, formatArea, formatLength } from '@rowted/shared'
import type { LayerColor } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Button } from '../../components/primitives.js'
import { ContextMenu } from '../../components/context-menu.js'
import { ConfirmDialog } from '../../components/overlays.js'
import { IconCamera, IconClose, IconMore, IconRuler } from '../../frame/icons.js'
import type { MapFeature } from '../api.js'

export function ShapePopup({
  feature, layerColor, canEdit, offerMeasure, at, sheet,
  onChange, onDelete, onClose, onSaveMeasurement,
}: {
  feature: MapFeature
  /** What it inherits when it has no colour of its own. */
  layerColor: LayerColor
  canEdit: boolean
  offerMeasure: boolean
  /** Where to hang it, in screen pixels inside the canvas. */
  at: { x: number; y: number }
  onChange: (patch: { name?: string; note?: string | null; color?: LayerColor | null }) => void
  /** Drawn in a bottom sheet rather than anchored to the shape (§6.3). */
  sheet?: boolean
  onDelete: () => void
  onClose: () => void
  onSaveMeasurement?: () => void
}) {
  const [name, setName] = useState(feature.name ?? '')
  const [note, setNote] = useState(feature.note ?? '')
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  const [confirming, setConfirming] = useState(false)

  /* A different shape means different words, without remounting the popup. */
  useEffect(() => {
    setName(feature.name ?? '')
    setNote(feature.note ?? '')
  }, [feature.id, feature.name, feature.note])

  const colour = feature.color ?? layerColor
  const measures = feature.kind !== 'marker' && feature.kind !== 'label'

  return (
    <div
      /*
       * §6.3: a phone has no popover anchored to a shape — a finger is on
       * the shape. The same body goes in a bottom sheet there, so the
       * anchored box is dropped rather than the component being written
       * twice (`components/sheet-or-dialog.tsx` makes the same argument for the
       * dialogs).
       */
      className={cn(
        sheet && 'w-full',
        !sheet && 'absolute z-10 w-[310px] overflow-hidden rounded-touch border border-line',
        !sheet && 'bg-surface shadow-popover',
      )}
      {...(sheet ? {} : { style: { left: Math.max(8, at.x), top: Math.max(8, at.y) } })}
    >
      <div className="flex items-center gap-2 border-b border-line-soft px-3 py-2.5">
        <span
          aria-hidden
          className="h-3 w-3 shrink-0 rounded-tile-small"
          style={{ background: LAYER_PALETTE[colour] }}
        />
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => { if (name !== (feature.name ?? '')) onChange({ name }) }}
          readOnly={!canEdit}
          placeholder="Name this shape"
          className={cn(
            'min-w-0 flex-1 bg-transparent text-cell font-bold text-text outline-none',
            'placeholder:font-medium placeholder:text-text-faint',
          )}
        />
        {canEdit && (
          <button
            type="button"
            onClick={(event) => setMenuAt({ x: event.clientX, y: event.clientY })}
            aria-label="Shape options"
            className="shrink-0 text-text-faint hover:text-text"
          >
            <IconMore size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 text-text-faint hover:text-text"
        >
          <IconClose size={14} />
        </button>
      </div>

      <div className="flex flex-col gap-2 px-3 py-2.5 text-[12px]">
        {measures && (
          <div className="flex gap-3.5">
            {feature.areaSqft !== null && (
              <span>
                <span className="text-text-muted">Area</span>
                {' '}
                <b className="tabular-nums">{formatArea(feature.areaSqft, 'ft')}</b>
              </span>
            )}
            {feature.lengthFt !== null && (
              <span>
                <span className="text-text-muted">
                  {feature.kind === 'line' ? 'Length' : 'Perimeter'}
                </span>
                {' '}
                <b className="tabular-nums">{formatLength(feature.lengthFt)}</b>
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <span className="w-11 shrink-0 text-text-muted">Colour</span>
          {LAYER_COLORS.map((one) => (
            <button
              key={one}
              type="button"
              disabled={!canEdit}
              aria-label={one}
              aria-pressed={one === colour}
              onClick={() => onChange({ color: one })}
              className="h-4 w-4 rounded-tag"
              /*
               * The ring is the mockup's own: a 2px gap in the surface colour
               * then a 1.5px halo in the swatch's own colour. Tailwind's offset
               * ring would need that gap colour as a token of its own, and the
               * halo *is* the swatch — a per-row value only a style can carry.
               */
              style={{
                background: LAYER_PALETTE[one],
                ...(one === colour
                  ? {
                    boxShadow: '0 0 0 2px rgb(var(--color-surface)),'
                      + ` 0 0 0 3.5px ${LAYER_PALETTE[one]}`,
                  }
                  : {}),
              }}
            />
          ))}
        </div>

        <label className="flex items-start gap-1.5">
          <span className="w-11 shrink-0 pt-[5px] text-text-muted">Note</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onBlur={() => { if (note !== (feature.note ?? '')) onChange({ note: note || null }) }}
            readOnly={!canEdit}
            rows={2}
            placeholder="What the crew needs to know here"
            className={cn(
              'flex-1 resize-y rounded-[6px] border border-line bg-field px-2 py-[5px]',
              'text-[11.5px] leading-[1.4] text-text-body placeholder:text-text-faint',
            )}
          />
        </label>

        {canEdit && (
          <div className="flex items-center gap-1.5">
            <span className="w-11 shrink-0 text-text-muted">Photo</span>
            {/* Attaching one is the ordinary file upload (§4.2); the button
                arrives with the uploader in the phone pass. */}
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-text-faint">
              <IconCamera size={13} />
              {feature.photoFileId ? 'One photo attached' : 'No photo yet'}
            </span>
          </div>
        )}
      </div>

      {menuAt && (
        <ContextMenu
          x={menuAt.x}
          y={menuAt.y}
          onClose={() => setMenuAt(null)}
          items={[{
            key: 'delete',
            label: 'Delete shape',
            tone: 'danger',
            onSelect: () => {
              setMenuAt(null)
              /* Confirm only where something somebody wrote would go with it
                 (§5.3). A bare outline is one Undo away. */
              if (feature.note || feature.photoFileId) setConfirming(true)
              else onDelete()
            },
          }]}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title={`Delete ${feature.name || 'this shape'}?`}
          body="Its note and photo go with it. Nothing else on the map changes."
          confirmLabel="Delete shape"
          onCancel={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); onDelete() }}
        />
      )}

      <div className="flex gap-1.5 border-t border-line-soft px-3 pb-2.5 pt-2">
        {canEdit && offerMeasure && measures && onSaveMeasurement && (
          <Button variant="primary" className="h-7 text-[11.5px]" onClick={onSaveMeasurement}>
            <IconRuler size={12} />
            Save to property measurements
          </Button>
        )}
        <Button className="ml-auto h-7 text-[11.5px]" onClick={onClose}>Done</Button>
      </div>
    </div>
  )
}
